"use client";

import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import LogoAnimatedGreeks from "@/components/logos/LogoAnimatedGreeks";
import GreekParticleBackground from "@/components/GreekParticleBackground";

import Modal from "@/components/Modal";
import { captureTermsAcceptance } from "@/lib/termsAcceptance";
import { useTrialDuration } from "@/hooks/useTrialDuration";

const IconUser = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const IconMail = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);
const IconLockKeyhole = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="16" r="1" /><rect x="3" y="10" width="18" height="12" rx="2" /><path d="M7 10V7a5 5 0 0 1 10 0v3" />
  </svg>
);
const IconUserPlus = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M2 21a8 8 0 0 1 13.292-6" /><circle cx="10" cy="8" r="5" /><path d="M19 16v6M22 19h-6" />
  </svg>
);
const IconArrowLeft = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="19" x2="5" y1="12" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);
const IconZap = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

function RegisterForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const source = searchParams.get('source'); // Get source parameter from URL
  const trialDays = useTrialDuration();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    passwordConfirm: "",
    name: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    title: string;
    content: React.ReactNode;
    onClose: () => void;
  }>({
    isOpen: false,
    title: "",
    content: null,
    onClose: () => { },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate password length — matches the PocketBase users collection's
    // min:8 constraint, so we never round-trip a request we know will fail
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.passwordConfirm) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      // Capture T&C acceptance data BEFORE creating account
      const termsAcceptance = await captureTermsAcceptance('registration');

      // Call backend API to create user (server-side PocketBase call)
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          passwordConfirm: formData.passwordConfirm,
          name: formData.name,
          termsAcceptance,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle error from backend
        if (data.code === 'DUPLICATE_EMAIL') {
          throw { isDuplicate: true, message: data.error };
        }
        throw new Error(data.error || 'Failed to create account');
      }

      // Show success modal
      setModalConfig({
        isOpen: true,
        title: "Welcome to 0DTE Toolbox",
        content: (
          <>
            <p className="text-lg">Account created successfully!</p>
            <p className="mt-2 text-gray-400">
              Please check your email to verify your account before logging in.
            </p>
          </>
        ),
        onClose: () => {
          setModalConfig((prev) => ({ ...prev, isOpen: false }));
          router.push("/login");
        },
      });

    } catch (err: any) {
      // Check for duplicate email error
      const isDuplicate = err.isDuplicate || err.code === 'DUPLICATE_EMAIL';

      if (isDuplicate) {
        setModalConfig({
          isOpen: true,
          title: "Account Already Exists",
          content: (
            <div className="flex flex-col gap-4">
              <p>
                The email address <strong className="text-white">{formData.email}</strong>
                {' '}
                is already registered.
              </p>
              <p className="text-sm text-gray-400">
                If this is you, you can{" "}
                <Link href="/login" className="!text-brand-blue font-bold underline decoration-brand-blue/30 hover:decoration-brand-blue transition-all">
                  Sign In
                </Link>{" "}
                or{" "}
                <Link href="/forgot-password" className="!text-brand-blue font-bold underline decoration-brand-blue/30 hover:decoration-brand-blue transition-all">
                  Reset Your Password
                </Link>
                .
              </p>
            </div>
          ),
          onClose: () => setModalConfig((prev) => ({ ...prev, isOpen: false })),
        });
      } else {
        setError(err.message || "Failed to create account. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const heading = source === 'classic'
    ? 'Register on 0DTE Toolbox'
    : source === 'trial'
      ? 'Start your free trial'
      : 'Create your account';

  return (
    <>
      {/* Global styles for LED glow animations */}
      <style jsx global>{`
        @keyframes pulse-glow-orange {
          0%, 100% {
            border-color: rgba(255, 152, 0, 0.4);
            box-shadow: 0 0 15px rgba(255, 152, 0, 0.3), 0 0 30px rgba(255, 152, 0, 0.1);
          }
          50% {
            border-color: rgba(255, 152, 0, 1);
            box-shadow: 0 0 20px rgba(255, 152, 0, 0.6), 0 0 40px rgba(255, 152, 0, 0.3);
          }
        }

        @keyframes pulse-glow-blue {
          0%, 100% {
            border-color: rgba(59, 130, 246, 0.4);
            box-shadow: 0 0 15px rgba(59, 130, 246, 0.3), 0 0 30px rgba(59, 130, 246, 0.1);
          }
          50% {
            border-color: rgba(59, 130, 246, 1);
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(59, 130, 246, 0.3);
          }
        }

        .animate-glow-orange {
          animation: pulse-glow-orange 2s ease-in-out infinite;
        }

        .animate-glow-blue {
          animation: pulse-glow-blue 2s ease-in-out infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-glow-orange, .animate-glow-blue {
            animation: none !important;
          }
        }
      `}</style>

      <div className="relative min-h-screen bg-brand-navy flex flex-col items-center justify-center p-4 md:p-8 overflow-hidden">
        <GreekParticleBackground />
        <div className="relative z-10 w-full max-w-5xl overflow-hidden rounded-2xl border border-white/10 bg-black/20 shadow-2xl shadow-black/40 lg:grid lg:grid-cols-2">

          {/* ── Left: Brand Panel (mirrors login's right panel) ─────────── */}
          <div className="relative hidden overflow-hidden bg-gradient-to-br from-brand-orange/80 via-brand-navy to-brand-blue lg:flex lg:flex-col lg:justify-center lg:px-12 lg:py-14">
            {/* Ambient glow accents */}
            <div className="pointer-events-none absolute -top-24 -left-16 h-72 w-72 rounded-full bg-brand-blue/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -right-16 h-72 w-72 rounded-full bg-brand-orange/40 blur-3xl" />

            <div className="relative z-10">
              <div className="mb-8 flex items-center gap-3">
                <div className="grid h-14 w-14 place-items-center rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm">
                  <IconUserPlus className="h-7 w-7 text-white" />
                </div>
                <div className="grid h-14 w-14 place-items-center rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm">
                  <span className="font-montserrat text-2xl font-extrabold text-white">Θ</span>
                </div>
              </div>

              <span className="text-xs font-bold uppercase tracking-widest text-white/70">
                Good to See You
              </span>
              <h2 className="mt-2 font-montserrat text-3xl font-bold tracking-tight text-white">
                Already have an account?
              </h2>
              <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/80">
                Your dashboards, signals, and API tokens are right where you left them. Sign in and pick up the thread.
              </p>

              <Link
                href="/login"
                className="mt-8 inline-flex items-center gap-2 rounded-lg border border-white/30 bg-white/10 px-5 py-3 text-sm font-bold text-white backdrop-blur-sm transition-all hover:bg-white/20"
              >
                <IconArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
              <p className="mt-3 font-mono text-[11px] text-white/60">
                Secure access · Always in sync
              </p>
            </div>
          </div>

          {/* ── Right: Form Panel ────────────────────────────────────────── */}
          <div className="flex flex-col justify-center px-6 py-10 sm:px-10 md:px-12 md:py-14">
            <Link href="/" className="mb-6 inline-flex items-center gap-3">
              <LogoAnimatedGreeks className="w-9 h-9" interval={3000} />
              <span className="font-montserrat text-lg font-bold text-white">
                ΘDTE <span className="text-brand-orange">Toolbox</span>
              </span>
            </Link>

            {/* Special welcome message based on source parameter */}
            {source === 'classic' && (
              <div className="mb-6 bg-gradient-to-r from-brand-blue/20 to-brand-orange/20 border-2 rounded-lg p-4 animate-glow-orange">
                <p className="text-brand-orange font-semibold text-sm mb-1 flex items-center gap-1.5">
                  <IconZap className="w-4 h-4" /> Welcome to the New Dashboard!
                </p>
                <p className="text-gray-300 text-xs">
                  You'll keep access to the classic dashboard you know.<br />
                  Plus get {trialDays} days FREE premium features here!
                </p>
              </div>
            )}

            {source === 'trial' && (
              <div className="mb-6 bg-gradient-to-r from-brand-blue/20 to-brand-orange/20 border-2 rounded-lg p-4 animate-glow-blue">
                <p className="text-brand-blue font-semibold text-sm mb-1 flex items-center gap-1.5">
                  <IconZap className="w-4 h-4" /> Start Your Free Trial
                </p>
                <p className="text-gray-300 text-xs">
                  Get {trialDays} days of premium features, absolutely FREE.<br />
                  No credit card required!
                </p>
              </div>
            )}

            <div className="mb-8">
              <span className="text-xs font-bold uppercase tracking-widest text-brand-blue">
                Start Building in Minutes
              </span>
              <h1 className="mt-2 font-montserrat text-3xl font-bold tracking-tight text-white">
                {heading}
              </h1>
            </div>

            {error && (
              <div className="mb-6 rounded-lg bg-red-500/10 border border-red-500/50 px-4 py-3 text-sm text-red-400">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-white mb-2">
                  Full name
                </label>
                <div className="relative">
                  <IconUser className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-700 bg-white/5 py-3 pl-10 pr-4 text-white placeholder-gray-500 transition-colors focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue"
                    placeholder="Ada Lovelace"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                  Email address
                </label>
                <div className="relative">
                  <IconMail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-700 bg-white/5 py-3 pl-10 pr-4 text-white placeholder-gray-500 transition-colors focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue"
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-white mb-2">
                  Password
                </label>
                <div className="relative">
                  <IconLockKeyhole className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    className="w-full rounded-lg border border-gray-700 bg-white/5 py-3 pl-10 pr-11 text-white placeholder-gray-500 transition-colors focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue"
                    placeholder="At least 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )
                    }
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1.5">Use 8+ characters with a number or symbol.</p>
              </div>

              <div>
                <label htmlFor="passwordConfirm" className="block text-sm font-semibold text-white mb-2">
                  Confirm password
                </label>
                <div className="relative">
                  <IconLockKeyhole className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                  <input
                    type={showPasswordConfirm ? "text" : "password"}
                    id="passwordConfirm"
                    name="passwordConfirm"
                    value={formData.passwordConfirm}
                    onChange={handleChange}
                    required
                    minLength={8}
                    className="w-full rounded-lg border border-gray-700 bg-white/5 py-3 pl-10 pr-11 text-white placeholder-gray-500 transition-colors focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue"
                    placeholder="Re-enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    {showPasswordConfirm ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="terms"
                  required
                  className="mt-1 mr-2 w-4 h-4 text-brand-blue bg-white/5 border-gray-600 rounded focus:ring-brand-blue flex-shrink-0"
                />
                <label htmlFor="terms" className="text-sm text-gray-400">
                  I agree to the{" "}
                  <Link href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:text-brand-blue/80 underline font-semibold">
                    Terms
                  </Link>{" "}
                  and{" "}
                  <Link href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:text-brand-blue/80 underline font-semibold">
                    Privacy Policy
                  </Link>
                  .
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-lg border border-white/10 bg-white/10 py-3.5 font-bold text-gray-300 shadow-lg shadow-black/20 transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:hover:translate-y-0 enabled:border-transparent enabled:bg-brand-orange enabled:text-white enabled:shadow-brand-orange/20 enabled:hover:bg-brand-orange/90 enabled:hover:shadow-brand-orange/40"
              >
                {loading
                  ? "Creating account..."
                  : "Create account"}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-400">
              Already have an account?{" "}
              <Link href="/login" className="font-semibold text-brand-orange transition-colors hover:text-brand-orange/80">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-gray-500">
          Real-Time Options Analytics · No credit card required · Start with Basic tier
        </p>

        <Modal
          isOpen={modalConfig.isOpen}
          onClose={modalConfig.onClose}
          title={modalConfig.title}
        >
          {modalConfig.content}
        </Modal>
      </div>
    </>
  );
}

export default function RegisterPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-brand-navy flex items-center justify-center text-gray-400">
      Loading...</div>}>
      <RegisterForm />
    </Suspense>
  );
}
